/**
 * Builder for step elements
 */
import { StepElementBuilderInterface } from '../element-interfaces';
import { BaseBuilder } from './BaseBuilder';
import { BlockElement } from '../../BlockRenderer/types';
import { deepClone } from '../utils';

/**
 * Builder for creating step elements
 * 
 * @example
 * ```typescript
 * const step = Elements.Step()
 *   .addElement(Elements.Text().text('Step content').build())
 *   .build();
 * ```
 */
export class StepElementBuilder extends BaseBuilder<BlockElement, StepElementBuilderInterface> implements StepElementBuilderInterface {
  /**
   * Creates a new StepElementBuilder instance
   */
  constructor() {
    super({
      type: 'step',
      elements: []
    });
  }

  /**
   * Creates a new StepElementBuilder instance
   */
  static builder(): StepElementBuilderInterface {
    return new StepElementBuilder();
  }

  /**
   * Adds an element to the step
   * 
   * @param element - Element to add to the step
   */
  addElement(element: BlockElement | { build(): BlockElement }): StepElementBuilderInterface {
    const copy = this.copy();
    const elementJson = 'build' in element ? element.build() : deepClone(element);
    copy.data.elements = [...(copy.data.elements as BlockElement[] || []), elementJson];
    return copy;
  }

  /**
   * Builds and returns the final step element
   */
  build(): BlockElement {
    if (!this.data.elements || (this.data.elements as BlockElement[]).length === 0) {
      throw new Error('Step must have at least one element');
    }

    return {
      type: 'step',
      elements: this.data.elements as BlockElement[]
    };
  }
}
